import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { getLocationDetails, getStaffLocations } from '../../services/api';
import { getLogoUrlFromMedia } from '../../constants';
import { pickTimeZoneCdFromLocationPayload } from '../../utils/locationPayload';
import { useDesktopTopBarProps } from '../../context/DesktopLayoutContext';
import { useOwnerViewOptional, setOwnerLocationCount } from '../../context/OwnerViewContext';
import { useNavigationGuard } from '../../context/NavigationGuardContext';
import { useRestaurantDetailsStore } from '../../redux/productCatalog/productCatalogHooks';
import { theme as T } from '../../theme';
import { locationDetailsCache, locationDetailsInFlight } from '../../services/locationDetailsCache';
import Sidebar from './Sidebar';
import TopBar from './TopBar';

// Cache staff-location responses to avoid repeated calls on route changes.
// Location detail responses use the shared cache in services/locationDetailsCache.
const staffLocationsCache = new Map<string, any[]>();
const staffLocationsInFlight = new Map<string, Promise<any[]>>();

const getInitials = (name: any) => {
    if (!name || typeof name !== 'string') return '—';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    return name.slice(0, 2).toUpperCase();
};

function DesktopLayoutInner({ children }: { children: React.ReactNode }) {
    const topBarProps = useDesktopTopBarProps();
    const navigate = useNavigate();
    const { guardedNavigate } = useNavigationGuard();
    const location = useLocation();
    const { auth, logout } = useAuth();
    const { selectedBranch: branch, setSelectedBranch } = useBranch();
    const { setRestaurantDetails } = useRestaurantDetailsStore();
    const ownerView = useOwnerViewOptional();
    const [outletOpen, setOutletOpen] = useState(false);
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [locationBranches, setLocationBranches] = useState<any[]>([]);

    const locationId = branch?.id ?? branch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    // On native, `auth` and `branch` hydrate asynchronously from secure storage, so on
    // the first paints we have no resolved outlet yet. Until the branch carries a real
    // name we show shimmer in the sidebar instead of flashing the "Select outlet"
    // placeholder. Derived synchronously so the shimmer is present from the very first
    // render (no flash → shimmer → value sequence). DesktopLayout only mounts when
    // authenticated, so an unresolved outlet always means "still loading", never "none".
    const outletResolved = Boolean(branch?.locationName ?? branch?.name ?? branch?.branchName);
    const outletLabel = branch?.locationName ?? branch?.name ?? branch?.branchName ?? 'Select outlet';
    const outletNameOnly = outletLabel.includes(',') ? outletLabel.split(',').pop().trim() : outletLabel;
    const outletHeaderName = outletLabel.includes(',') ? outletLabel.split(',')[0].trim() : outletLabel;
    const displayName = auth?.fullName ?? auth?.userId ?? auth?.userName ?? 'User';
    const userRole = auth?.role ?? auth?.designation ?? 'Restaurant Manager';
    const userInitials = getInitials(displayName);

    const selectBranch = (loc: any) => {
        const id = loc.id ?? loc.locationId;
        const name = loc.locationName ?? loc.name ?? 'Branch';
        const logoUrl = getLogoUrlFromMedia(loc?.media) || loc?.logoUrl || '';
        const timeZoneCd =
            pickTimeZoneCdFromLocationPayload(loc) ?? (loc?.timeZoneCd as string | undefined);
        setSelectedBranch({
            ...loc,
            id: loc.id ?? id,
            locationName: loc.locationName ?? loc.name ?? name,
            logoUrl,
            ...(timeZoneCd ? { timeZoneCd } : {}),
        });
        setOutletOpen(false);
    };

    const applyLocationDetailsToBranch = (payload: any, currentId: any) => {
        const list = payload?.branch ?? payload?.branches ?? [];
        const hasList = Array.isArray(list) && list.length > 0;
        // Store all branches for the outlet switcher
        if (hasList) {
            const normalised = list.map((item: any) => ({
                ...item,
                locationId: item.id ?? item.locationId,
                locationName: item.name ?? item.locationName ?? item.label ?? '',
            }));
            setLocationBranches(normalised);
        }
        const match = hasList ? list.find((loc) => (loc.id ?? loc.locationId) === currentId) : null;
        // API may return single location details with media at top level (like restaurantDetails)
        const singleDetails = !hasList && payload?.media && Array.isArray(payload.media) ? payload : null;
        const source = match ?? singleDetails;
        if (!source) return;
        const logoUrl = getLogoUrlFromMedia(source.media) || source.logoUrl || '';
        const locationName = source.locationName ?? source.name ?? source.branchName ?? branch?.locationName;
        const timeZoneCd =
            pickTimeZoneCdFromLocationPayload(payload) ?? pickTimeZoneCdFromLocationPayload(source);
        setSelectedBranch({
            ...branch,
            ...source,
            id: source.id ?? source.locationId ?? currentId,
            locationName,
            logoUrl: logoUrl || branch?.logoUrl,
            ...(timeZoneCd ? { timeZoneCd } : {}),
            workingHours: payload?.workingHours || source?.workingHours || branch?.workingHours || [],
            // `source` (matched item from payload.branch/branches) is not a reliable
            // source of country — some environments omit it from every branch[] entry.
            // Only the top-level payload.country is consistently correct, so use ONLY
            // that (no fallback to source/branch) — every report screen's currency
            // formatting reads `branch?.country`, so this must resolve to the top-level
            // API value, never a stale or nested one.
            country: payload?.country,
            uiFeatureFlags: payload?.uiFeatureFlags ?? source?.uiFeatureFlags ?? branch?.uiFeatureFlags,
        });
    };

    // Fetch logo / details from GET /merchants/location/{id}/details. Ignore stale responses when the user switches branches quickly.
    useEffect(() => {
        if (!locationId || !token) return;
        let cancelled = false;
        const currentId = branch?.id ?? branch?.locationId ?? locationId;
        const cacheKey = String(locationId);

        const cachedPayload = locationDetailsCache.get(cacheKey);
        if (cachedPayload) {
            setRestaurantDetails((cachedPayload?.restaurantDetails ?? cachedPayload) as Record<string, unknown>);
            applyLocationDetailsToBranch(cachedPayload, currentId);
            return () => { cancelled = true; };
        }

        const inFlight = locationDetailsInFlight.get(cacheKey);
        if (inFlight) {
            inFlight
                .then((payload) => {
                    if (cancelled || !payload) return;
                    setRestaurantDetails((payload?.restaurantDetails ?? payload) as Record<string, unknown>);
                    applyLocationDetailsToBranch(payload, currentId);
                })
                .catch(() => {});
            return () => { cancelled = true; };
        }

        const req = getLocationDetails(locationId, token)
            .then((res) => {
                const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
                if (payload) locationDetailsCache.set(cacheKey, payload);
                return payload;
            })
            .finally(() => {
                locationDetailsInFlight.delete(cacheKey);
            });
        locationDetailsInFlight.set(cacheKey, req);

        req
            .then((payload) => {
                if (cancelled || !payload) return;
                setRestaurantDetails((payload?.restaurantDetails ?? payload) as Record<string, unknown>);
                applyLocationDetailsToBranch(payload, currentId);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [locationId, token]);

    const staffId = auth?.id ?? '';
    useEffect(() => {
        if (!staffId || !token) return;
        const cacheKey = String(staffId);
        const cached = staffLocationsCache.get(cacheKey);
        if (cached) {
            setOwnerLocationCount(cached.length);
            setLocations(cached);
            setLocationsLoading(false);
            return;
        }

        setLocationsLoading(true);

        const inFlight = staffLocationsInFlight.get(cacheKey);
        if (inFlight) {
            inFlight
                .then((normalised) => { setOwnerLocationCount(normalised.length); setLocations(normalised); })
                .catch(() => setLocations([]))
                .finally(() => setLocationsLoading(false));
            return;
        }

        const req = getStaffLocations(staffId, token)
            .then((res) => {
                const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
                const list = Array.isArray(payload) ? payload : payload?.branch ?? payload?.branches ?? [];
                const normalised = list.map((item) => ({
                    ...item,
                    locationId: item.id ?? item.locationId,
                    locationName: item.name ?? item.locationName ?? item.label ?? '',
                }));
                staffLocationsCache.set(cacheKey, normalised);
                return normalised;
            })
            .finally(() => {
                staffLocationsInFlight.delete(cacheKey);
            });
        staffLocationsInFlight.set(cacheKey, req);

        req
            .then((normalised) => { setOwnerLocationCount(normalised.length); setLocations(normalised); })
            .catch(() => setLocations([]))
            .finally(() => setLocationsLoading(false));
    }, [staffId, token]);

    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };

    const pathname = location.pathname || '';
    const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];
    const isSuperUser = SUPER_ROLES.includes(userRole);

    // Disable branch dropdown on certain routes (matches legacy merchant_portal SidePanel)
    const disabledPaths = ['/employees/add', '/employees/edit/', '/product-catalog/', '/display/create', '/display/edit'];
    const isDropdownDisabledByRoute = disabledPaths.some((p) => pathname.includes(p));
    const isDropdownDisabled = isDropdownDisabledByRoute || !isSuperUser;

    const effectiveLocations = ownerView?.allLocations?.length > 0 ? ownerView.allLocations : locations;
    const effectiveLoading = ownerView ? ownerView.locationsLoading : locationsLoading;

    // Prefer locationBranches (from getLocationDetails) when it has more entries than getStaffLocations —
    // getLocationDetails returns all sub-branches under the merchant, getStaffLocations may return only 1.
    const outletSourceLocations = locationBranches.length > effectiveLocations.length ? locationBranches : effectiveLocations;

    const outletOptions = isDropdownDisabled ? [] : (outletSourceLocations || []).map((loc) => {
        const locName = loc.locationName ?? loc.name ?? loc.branchName ?? 'Branch';
        const locNameOnly = locName.includes(',') ? locName.split(',').pop().trim() : locName;
        return { id: loc.id ?? loc.locationId, label: locNameOnly, raw: loc };
    });

    // The sidebar outlet switcher only changes the globally-selected branch.
    // It must NOT drive the Owner Dashboard's own brand/store dropdowns, so we
    // deliberately avoid ownerView.changeStore here (that would mutate
    // selectedStore, which the Owner Dashboard filters bind to).
    const handleOutletSelect = (opt: any) => {
        selectBranch(opt.raw);
    };

    return (
        <div
            style={{
                fontFamily: T.f,
                background: T.bg,
                height: '100%',
                minHeight: '100vh',
                maxHeight: '100vh',
                overflow: 'hidden',
                display: 'flex',
                color: T.t1,
                WebkitFontSmoothing: 'antialiased',
            }}
        >
            <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" rel="stylesheet" />
            <style>{`.material-symbols-rounded { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; display: inline-flex; align-items: center; justify-content: center; }`}</style>
            <Sidebar
                brand={{
                    logoLetters: (outletHeaderName || 'M').slice(0, 2).toUpperCase(),
                    brandName: outletHeaderName || 'Maghil',
                    subtitle: 'OUTLET',
                }}
                outlet={{
                    label: outletNameOnly || 'Select branch',
                    name: outletHeaderName || outletLabel || 'Outlet',
                    logoUrl: branch?.logoUrl ?? null,
                    location: branch?.city ?? branch?.address ?? branch?.location ?? (outletNameOnly || ''),
                    readOnlyLabelOnly: isDropdownDisabledByRoute,
                    options: outletOptions,
                    selectedId: branch?.id ?? branch?.locationId,
                    onSelect: handleOutletSelect,
                    open: outletOpen,
                    onToggle: () => setOutletOpen(!outletOpen),
                    loading: effectiveLoading,
                    detailsLoading: !outletResolved,
                    isCateringEnabled: branch?.uiFeatureFlags?.isCateringEnabled === true,
                }}
                pathname={pathname}
                navigate={guardedNavigate}
                user={{ initials: userInitials, name: displayName, role: userRole, onLogout: handleLogout }}
            />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <TopBar
                    leftAddon={topBarProps.leftAddon}
                    breadcrumbs={topBarProps.breadcrumbs}
                    title={topBarProps.title}
                    dateFilters={topBarProps.dateFilters}
                    sourceFilters={topBarProps.sourceFilters}
                    extraContent={topBarProps.extraContent}
                    onExport={topBarProps.onExport}
                />
                <div data-desktop-main-scroll style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

/**
 * Fixed desktop layout: Sidebar + TopBar are fixed; children render in the remaining space.
 * Used only when isDesktop; on mobile, different screens render without this layout.
 */
export default function DesktopLayout({ children }: { children: React.ReactNode }) {
    return <DesktopLayoutInner>{children}</DesktopLayoutInner>;
}
